/**
 * ติดตามรับพนักงานใหม่ — แดชบอร์ดคุมพนักงานใหม่ทั้งกอง: กี่คนกำลังรับเข้า · กี่คนอยู่ใน
 * ขั้นตอนเซ็นเอกสาร · ใครเลยกำหนดแล้ว · อีกกี่วันต้องเสร็จอะไร
 * + เช็คลิสต์รายคน (auto-tick จากระบบ + ติ๊กมือ) + เริ่มรับเข้า + ทวง + จบการรับเข้า
 *
 * ⭐ ฟอร์มที่เลือกใช้: การ์ดตัวเลข + รายการเรียงตามความด่วน — ไม่ใช่กราฟ
 * (คนหลักสิบ กราฟไม่ช่วยตัดสินใจ สิ่งที่ HR ต้องรู้คือ "วันนี้ต้องตามใครก่อน")
 * ⭐ ป้ายสถานะมีข้อความกำกับเสมอ ไม่ใช้สีอย่างเดียว
 */
import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { Modal } from '../../components/ui/Modal';
import {
  useOnboardingOverview,
  useOnboardingDetail,
  useMarkOnboardingItem,
  useStartOnboarding,
  useCompleteOnboarding,
  useCancelOnboarding,
  useRemindOnboarding,
  useReissuePairing,
  useEmployees,
  type OnboardingDetailItem,
  type OnboardingOverviewRow,
  type OnboardingSummary,
} from '../../hooks';
import {
  ONBOARDING_CATEGORY_LABELS,
  EMPLOYEE_DOC_TYPE_LABELS,
  dueLabel,
  dueUrgencyRank,
  type OnboardingCategory,
  type OnboardingDueState,
  type EmployeeDocType,
} from '@payroll/shared';
import { UserPlus, Bell, CheckCircle2, Circle, ChevronRight, AlertTriangle, Clock, PenLine, Users, Share2, Link2 } from 'lucide-react';
import { buildPairMessage, shareOrCopyInvite } from '../../lib/pair-invite';
import { useAuthStore } from '../../lib/store';

/** โชว์ป้าย "ยังค้าง" กี่ข้อบนการ์ดก่อนย่อเป็น "และอีก N รายการ" — ยาวพรืดแล้วอ่านไม่ออก */
const MISSING_PREVIEW = 4;

/** ตัวกรองด้านบน — ค่าเดียวกับที่การ์ดตัวเลขนับ กดการ์ดแล้วกรองตรงกันเสมอ */
type BoardFilter = 'all' | 'overdue' | 'awaiting_sign' | 'complete' | 'no_start_date';

interface EmployeeBasic {
  emp_code: string;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  status: string;
}

function fullName(e: EmployeeBasic): string {
  return `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.nickname || e.emp_code;
}

/** สีป้ายตามสถานะความด่วน — ชุดสถานะ ไม่ใช่สีชุดเดียวกับแบรนด์ (กันอ่านผิดว่าเป็นหมวดหมู่) */
function dueChipClass(state: OnboardingDueState): string {
  switch (state) {
    case 'overdue': return 'bg-red-100 text-red-700';
    case 'due_today': return 'bg-orange-100 text-orange-800';
    case 'due_soon': return 'bg-amber-100 text-amber-800';
    case 'no_start_date': return 'bg-gray-200 text-gray-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

function docLabel(t: string): string {
  return EMPLOYEE_DOC_TYPE_LABELS[t as EmployeeDocType] ?? t;
}

/** การ์ดตัวเลขหัวหน้าจอ — กดเพื่อกรองรายการด้านล่าง */
function StatTile({ label, value, tone, icon: Icon, active, onClick }: {
  label: string;
  value: number;
  tone: 'neutral' | 'critical' | 'warning' | 'good';
  icon: typeof Users;
  active: boolean;
  onClick: () => void;
}) {
  const toneRing = tone === 'critical' ? 'text-red-700' : tone === 'warning' ? 'text-amber-700' : tone === 'good' ? 'text-green-700' : 'text-gray-900';
  return (
    <button
      onClick={onClick}
      className={`card p-3 text-left transition ${active ? 'ring-2 ring-brand-500' : 'hover:bg-gray-50'}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="whitespace-nowrap">{label}</span>
      </div>
      <div className={`text-2xl font-semibold leading-none ${toneRing}`}>{value}</div>
    </button>
  );
}

export function OwnerOnboarding() {
  const { data: overview, isLoading } = useOnboardingOverview();
  const { data: allEmployees } = useEmployees() as { data: EmployeeBasic[] | undefined };

  const [selectedEmp, setSelectedEmp] = useState<string | null>(null);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [filter, setFilter] = useState<BoardFilter>('all');

  const employees = overview?.employees ?? [];
  const activeCodes = useMemo(() => new Set(employees.map((e) => e.emp_code)), [employees]);
  // รวม pending_start (รอยืนยันตัวตน/รอเริ่มงาน) ด้วย — onboarding = เก็บเอกสาร/ตั้งค่า
  // ก่อนเริ่มงานจริง จึงควรเริ่มรับเข้าได้ตั้งแต่ยังไม่ยืนยันตัวตน. ตัด ลาออก/เลิกจ้าง ออก.
  const startCandidates = (allEmployees ?? []).filter(
    (e) => ['active', 'probation', 'pending_start'].includes(e.status) && !activeCodes.has(e.emp_code),
  );

  const summary: OnboardingSummary = overview?.summary ?? {
    onboarding: employees.length, awaiting_sign: 0, overdue: 0, due_soon: 0, complete: 0, no_start_date: 0,
  };

  // เรียง "ต้องตามใครก่อน" ขึ้นบนสุดเสมอ — เลยกำหนด → ครบวันนี้ → ใกล้ครบ → ที่เหลือ
  const shown = useMemo(() => {
    const pass = (e: OnboardingOverviewRow) => {
      const complete = e.required_count > 0 && e.done_count >= e.required_count;
      switch (filter) {
        case 'overdue': return e.overdue_count > 0;
        case 'awaiting_sign': return e.awaiting_sign.length > 0;
        case 'complete': return complete;
        case 'no_start_date': return !e.start_date;
        default: return true;
      }
    };
    return employees.filter(pass).sort((a, b) => {
      const ra = dueUrgencyRank(a.next_due?.due_state ?? 'done');
      const rb = dueUrgencyRank(b.next_due?.due_state ?? 'done');
      if (ra !== rb) return ra - rb;
      return (a.next_due?.days_left ?? 9999) - (b.next_due?.days_left ?? 9999);
    });
  }, [employees, filter]);

  return (
    <AdminLayout title="ติดตามรับพนักงานใหม่">
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-sm text-gray-600">
          คุมพนักงานใหม่ทั้งกอง — ใครค้างอะไร อีกกี่วันต้องเสร็จ ใครเลยกำหนดแล้ว
        </p>
        <button onClick={() => setShowStartPicker(true)} className="btn-cta whitespace-nowrap flex-shrink-0">
          <UserPlus className="w-4 h-4" /> เริ่มรับพนักงานใหม่
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <StatTile label="กำลังรับเข้า" value={summary.onboarding} tone="neutral" icon={Users}
          active={filter === 'all'} onClick={() => setFilter('all')} />
        <StatTile label="รอเซ็นเอกสาร" value={summary.awaiting_sign} tone="warning" icon={PenLine}
          active={filter === 'awaiting_sign'} onClick={() => setFilter(filter === 'awaiting_sign' ? 'all' : 'awaiting_sign')} />
        <StatTile label="เลยกำหนดแล้ว" value={summary.overdue} tone="critical" icon={AlertTriangle}
          active={filter === 'overdue'} onClick={() => setFilter(filter === 'overdue' ? 'all' : 'overdue')} />
        <StatTile label="ครบแล้ว รอปิด" value={summary.complete} tone="good" icon={CheckCircle2}
          active={filter === 'complete'} onClick={() => setFilter(filter === 'complete' ? 'all' : 'complete')} />
      </div>

      {summary.no_start_date > 0 && (
        <button
          onClick={() => setFilter(filter === 'no_start_date' ? 'all' : 'no_start_date')}
          className="w-full text-left rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 mb-3"
        >
          <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
          {summary.no_start_date} คนยังไม่ระบุวันเริ่มงาน — คำนวณกำหนดวันครบให้ไม่ได้ กดดูรายชื่อ
        </button>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : employees.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีพนักงานที่กำลังรับเข้าตอนนี้</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีคนในกลุ่มนี้ — กด "กำลังรับเข้า" เพื่อดูทั้งหมด</p>
      ) : (
        <div className="space-y-2">
          {shown.map((e) => {
            const pct = e.required_count > 0 ? Math.round((e.done_count / e.required_count) * 100) : 100;
            const complete = e.required_count > 0 && e.done_count >= e.required_count;
            // ค้างข้อไหน — บอกบนการ์ดเลย ไม่ต้องกดเข้าไปดูทีละคน (เห็นแค่ 6/7 แล้วเดาไม่ออก)
            const missing = (e.items ?? []).filter((it) => it.required && !it.done);
            const next = e.next_due;
            const barTone = complete ? 'bg-green-500' : e.overdue_count > 0 ? 'bg-red-500' : 'bg-brand-500';
            return (
              <button
                key={e.emp_code}
                onClick={() => setSelectedEmp(e.emp_code)}
                className="card w-full text-left p-3 flex items-center gap-3 hover:bg-gray-50 transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {e.name}
                    {complete && <span className="text-xs text-green-600 font-semibold">ครบแล้ว</span>}
                    {/* งานที่ต้องทำถัดไป + อีกกี่วัน — ข้อความเต็ม ไม่ใช้สีบอกอย่างเดียว */}
                    {!complete && next && (
                      <span className={`inline-block text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap font-normal ${dueChipClass(next.due_state)}`}>
                        {next.label} — {dueLabel({ state: next.due_state, dueDate: next.due_date, daysLeft: next.days_left })}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mb-1">
                    {e.emp_code}
                    {e.department ? ` · ${e.department}` : ''}
                    {e.start_date ? ` · เริ่มงาน ${e.start_date}` : ' · ยังไม่ระบุวันเริ่มงาน'}
                  </div>
                  {/* ⭐ ยังไม่ผูก LINE = ระบบทวงเขาไม่ได้เลย ต้องบอกตรงนี้ ไม่ใช่ให้กดปุ่มทวง
                      แล้วค่อยขึ้นว่าทวงไม่ได้ (HR ไม่มีทางรู้ว่าต้องตามคนนี้เอง) */}
                  {e.line_linked === false && (
                    <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mb-1 inline-flex items-center gap-1">
                      <Link2 className="w-3 h-3 flex-shrink-0" />
                      <span className="whitespace-nowrap">ยังไม่ผูก LINE — ทวงในระบบไม่ได้ ต้องส่งคำเชิญเอง</span>
                    </div>
                  )}
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    {e.done_count}/{e.required_count} รายการที่จำเป็น
                    {e.awaiting_sign.length > 0 && (
                      <span className="text-amber-700"> · รอเซ็น {e.awaiting_sign.map(docLabel).join(', ')}</span>
                    )}
                  </div>
                  {missing.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">ยังค้าง</span>
                      {missing.slice(0, MISSING_PREVIEW).map((it) => (
                        <span
                          key={it.item_key}
                          className={`inline-block text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap ${dueChipClass(it.due_state)}`}
                        >
                          {it.label}
                          {it.due_state === 'overdue' ? ` (เลย ${Math.abs(it.days_left ?? 0)} วัน)` : ''}
                        </span>
                      ))}
                      {missing.length > MISSING_PREVIEW && (
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">
                          และอีก {missing.length - MISSING_PREVIEW} รายการ
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {selectedEmp && (
        <OnboardingDetailModal
          empCode={selectedEmp}
          empName={employees.find((e) => e.emp_code === selectedEmp)?.name ?? selectedEmp}
          onClose={() => setSelectedEmp(null)}
        />
      )}

      {showStartPicker && (
        <StartOnboardingModal
          candidates={startCandidates}
          onClose={() => setShowStartPicker(false)}
          onStarted={(empCode) => {
            setShowStartPicker(false);
            setSelectedEmp(empCode);
          }}
        />
      )}
    </AdminLayout>
  );
}

function StartOnboardingModal({
  candidates,
  onClose,
  onStarted,
}: {
  candidates: EmployeeBasic[];
  onClose: () => void;
  onStarted: (empCode: string) => void;
}) {
  const startM = useStartOnboarding();
  const toast = useToast();
  const [q, setQ] = useState('');
  const filtered = candidates.filter((e) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return fullName(e).toLowerCase().includes(s) || e.emp_code.toLowerCase().includes(s);
  });

  async function handleStart(empCode: string) {
    try {
      await startM.mutateAsync(empCode);
      toast.success('เริ่มรับพนักงานใหม่แล้ว');
      onStarted(empCode);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เริ่มไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title="เลือกพนักงานที่จะเริ่มรับเข้า">
      {candidates.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีพนักงานที่รอเริ่ม onboarding (ทุกคนกำลังทำอยู่แล้ว หรือทำเสร็จหมดแล้ว)</p>
      ) : (
        <div className="space-y-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นหาชื่อ / รหัสพนักงาน"
            className="input"
            autoFocus
          />
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-2">ไม่พบพนักงานตามคำค้น</p>
            ) : filtered.map((e) => (
              <button
                key={e.emp_code}
                onClick={() => handleStart(e.emp_code)}
                disabled={startM.isPending}
                className="w-full text-left border border-gray-100 rounded-lg px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
              >
                <span>
                  {fullName(e)} <span className="text-xs text-gray-400">{e.emp_code}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function OnboardingDetailModal({ empCode, empName, onClose }: { empCode: string; empName: string; onClose: () => void }) {
  const { data, isLoading } = useOnboardingDetail(empCode);
  const markM = useMarkOnboardingItem(empCode);
  const completeM = useCompleteOnboarding();
  const cancelM = useCancelOnboarding();
  const remindM = useRemindOnboarding();
  const reissueM = useReissuePairing();
  const consequence = useConsequence();
  const toast = useToast();
  const tenantCode = useAuthStore((s) => s.profile?.tenant_code ?? null);

  // ยังไม่ผูก LINE = ปุ่มทวงส่งไปไม่ถึงแน่นอน → เปลี่ยนเป็นเส้นทางที่ใช้ได้จริง
  const lineLinked = data?.line_linked !== false;

  const grouped = useMemo(() => {
    const byCat = new Map<OnboardingCategory, OnboardingDetailItem[]>();
    for (const it of data?.items ?? []) {
      const list = byCat.get(it.category) ?? [];
      list.push(it);
      byCat.set(it.category, list);
    }
    return byCat;
  }, [data]);

  async function handleToggle(itemKey: string, done: boolean) {
    try {
      await markM.mutateAsync({ item_key: itemKey, done });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleRemind() {
    try {
      const r = await remindM.mutateAsync(empCode);
      if (r.skipped === 'no_line') {
        toast.info(`ยังไม่ผูก LINE — ทวงไม่ถึง${r.pending_count ? ` (ค้าง ${r.pending_count} รายการ)` : ''} กด "ส่งคำเชิญผูก LINE" ด้านล่างแทน`);
      } else if (r.skipped === 'no_pending') toast.info('ไม่มีรายการที่ต้องทวงแล้ว');
      else toast.success(`ทวงพนักงานแล้ว (${r.pending_count} รายการ)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทวงไม่สำเร็จ');
    }
  }

  /**
   * ⭐ ทางออกของกับดักไก่กับไข่: คนที่ค้างข้อ "ผูกบัญชี LINE" ทวงผ่าน LINE ไม่ได้
   * ตลอดกาล → ออกรหัสจับคู่ใหม่ให้ แล้วให้ HR ส่งทางอื่น (แชท/โทร/บอกปากเปล่า)
   * รหัสสุ่ม + มีวันหมดอายุ ไม่ใช่เลขที่เดาได้
   */
  async function handleInvite() {
    if (!tenantCode) {
      toast.error('ไม่พบรหัสบริษัท — ดูที่หน้าตั้งค่า แล้วส่งรหัสจับคู่ให้พนักงานเอง');
      return;
    }
    try {
      const p = await reissueM.mutateAsync({ emp_code: empCode });
      const msg = buildPairMessage({
        name: empName,
        tenantCode,
        pairingCode: p.pairing_code,
        expiresAt: p.expires_at,
      });
      const how = await shareOrCopyInvite(msg);
      toast.success(
        how === 'shared'
          ? 'ส่งคำเชิญทาง LINE แล้ว'
          : 'คัดลอกข้อความเชิญแล้ว — วางส่งให้พนักงานทางไหนก็ได้',
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ออกรหัสจับคู่ไม่สำเร็จ');
    }
  }

  async function handleComplete() {
    const ok = await consequence({
      title: 'จบการรับเข้า',
      intro: `ยืนยันว่า ${empName} รับพนักงานใหม่เสร็จสิ้นแล้ว`,
      effects: ['ปิดสถานะ onboarding ของพนักงานคนนี้', 'ไม่แสดงในแดชบอร์ดรับพนักงานใหม่อีก'],
      confirmText: 'จบการรับเข้า',
    });
    if (!ok) return;
    try {
      await completeM.mutateAsync(empCode);
      toast.success('จบการรับเข้าแล้ว');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  async function handleCancel() {
    const ok = await consequence({
      title: 'เอาออกจากรายการติดตาม',
      intro: `เอา ${empName} ออกจากรายการติดตามรับพนักงานใหม่ (ใช้กับคนที่เพิ่มผิด หรือพนักงานทดสอบ)`,
      effects: [
        'ไม่ถือว่ารับเข้าสำเร็จ — ต่างจากปุ่ม "จบการรับเข้า"',
        'ไม่แสดงในรายการนี้อีก แต่ข้อมูลพนักงานยังอยู่ครบ',
        'รายการที่ติ๊กไว้แล้วยังเก็บอยู่ กด "เริ่มรับพนักงานใหม่" ใหม่ได้ของเดิมคืน',
      ],
      confirmText: 'เอาออกจากรายการ',
    });
    if (!ok) return;
    try {
      await cancelM.mutateAsync(empCode);
      toast.success('เอาออกจากรายการแล้ว');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  return (
    <Modal open onClose={onClose} title={empName} size="max-w-2xl">
      <div className="space-y-4">
        <div className="flex gap-2">
          {lineLinked ? (
            <button onClick={handleRemind} disabled={remindM.isPending} className="btn-secondary flex-1">
              <Bell className="w-4 h-4" /> ทวงพนักงาน
            </button>
          ) : (
            <button onClick={handleInvite} disabled={reissueM.isPending} className="btn-secondary flex-1">
              <Share2 className="w-4 h-4" /> ส่งคำเชิญผูก LINE
            </button>
          )}
          <button onClick={handleComplete} disabled={completeM.isPending} className="btn-cta flex-1">
            <CheckCircle2 className="w-4 h-4" /> จบการรับเข้า
          </button>
        </div>

        {data && !lineLinked && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            <Link2 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            <span className="font-medium">ยังไม่ผูก LINE — ระบบทวงเขาไม่ได้</span> เพราะช่องทางทวงคือ LINE เอง
            กด "ส่งคำเชิญผูก LINE" เพื่อออกรหัสจับคู่ใหม่ แล้วส่งให้เขาทางอื่น (แชท / โทรบอก)
            พอผูกเสร็จ ข้ออื่นที่ค้างอยู่จะทวงในระบบได้ตามปกติ
          </div>
        )}

        {(data?.awaiting_sign?.length ?? 0) > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            <PenLine className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            ส่งไปให้เซ็นแล้วแต่ยังไม่เซ็น: {(data?.awaiting_sign ?? []).map(docLabel).join(', ')} — กด "ทวงพนักงาน" ได้เลย
          </div>
        )}
        {data && !data.start_date && (
          <div className="rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 text-xs text-gray-700">
            ยังไม่ระบุวันเริ่มงาน — ระบบคำนวณกำหนดวันครบให้ไม่ได้ ไปกรอกที่ทะเบียนพนักงาน แท็บ "งาน"
          </div>
        )}

        <div className="text-right">
          <button
            onClick={handleCancel}
            disabled={cancelM.isPending}
            className="text-xs text-gray-500 underline hover:text-red-600 disabled:opacity-50"
          >
            เอาออกจากรายการ (เพิ่มผิดคน / พนักงานทดสอบ)
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400">กำลังโหลด…</p>
        ) : (
          [...grouped.entries()].map(([cat, items]) => (
            <div key={cat}>
              <div className="text-xs font-semibold text-gray-500 mb-1">{ONBOARDING_CATEGORY_LABELS[cat]}</div>
              <div className="space-y-1">
                {(items ?? []).map((it) => (
                  <div
                    key={it.item_key}
                    className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {it.auto_check ? (
                        it.done ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        )
                      ) : (
                        <input
                          type="checkbox"
                          checked={it.done}
                          onChange={(e) => handleToggle(it.item_key, e.target.checked)}
                          disabled={markM.isPending}
                          className="w-4 h-4 flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="truncate">
                          {it.label} {!it.required && <span className="text-gray-400 text-xs">(ไม่บังคับ)</span>}
                        </div>
                        <div className="text-[10px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                          {it.auto_check && <span>{it.done ? 'ระบบเช็คให้แล้ว' : 'รอระบบเช็คให้อัตโนมัติ'}</span>}
                          {/* กำหนดวันครบ — โชว์เฉพาะรายการที่ยังไม่เสร็จและตั้งกำหนดไว้ */}
                          {!it.done && it.due_state !== 'no_due' && (
                            <span className={`inline-block rounded px-1.5 py-0.5 whitespace-nowrap ${dueChipClass(it.due_state)}`}>
                              {dueLabel({ state: it.due_state, dueDate: it.due_date, daysLeft: it.days_left })}
                              {it.due_date ? ` (ครบ ${it.due_date})` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
